import React, { useEffect, useCallback } from "react";
import { Handle, Position, useReactFlow, useUpdateNodeInternals } from "@xyflow/react";
import { cn } from "../../../../lib/utils";
import { Input } from "../../../../components/ui/input";
import { Textarea } from "../../../../components/ui/textarea";
import { Button } from "../../../../components/ui/button";
import SvgIcon from "../../../../components/SvgIcon";
import NodeControl from "../components/node-control.js";
import { useNodeDataUpdate } from "../../hooks/use-node-data-update.js";

/** Same layout as Conversation node; distinct teal styling so it reads as a greeting node. */
const Greeting = ({
  id,
  data,
  selected,
}) => {
  const updateNodeInternals = useUpdateNodeInternals();
  const { updateNodeData, setEdges } = useReactFlow();
  const { handleNodeDataUpdate } = useNodeDataUpdate();

  const transition_text_onchange = useCallback(
    (index, text) => {
      const currentTransitions = data.transition || [];
      const newTransitions = [...currentTransitions];
      if (newTransitions[index]) {
        newTransitions[index] = {
          ...newTransitions[index],
          text: text,
        };
      }
      handleNodeDataUpdate({ id, data: { transition: newTransitions } });
    },
    [id, data.transition, handleNodeDataUpdate]
  );

  const transition_add = useCallback(() => {
    const currentTransitions = data.transition || [];
    const newTransitions = [...currentTransitions];
    updateNodeData(id, {
      transition: [
        ...newTransitions,
        {
          handleId: "transition-" + new Date().getTime(),
          text: "Describe the transition condition",
        },
      ],
    });
    updateNodeInternals(id);
  }, [data.transition, updateNodeData, id, updateNodeInternals]);

  const transition_del = useCallback(
    (index) => {
      const currentTransitions = data.transition || [];
      const toDelHandleId = currentTransitions[index].handleId;
      const newTransitions = currentTransitions.filter((_, i) => i !== index);
      setEdges((edges) =>
        edges.filter(
          (edge) => !(edge.sourceHandle === toDelHandleId && edge.source === id)
        )
      );
      updateNodeData(id, { transition: newTransitions });
      updateNodeInternals(id);
    },
    [data.transition, setEdges, updateNodeData, id, updateNodeInternals]
  );

  const handleBlur = useCallback(
    (key, value) => {
      const trimmedValue = value.trim();
      const normalizedValue = trimmedValue.replace(/\s+/g, " ");
      updateNodeData(id, { [key]: normalizedValue });
    },
    [id, updateNodeData]
  );

  const handleTransitionBlur = useCallback(
    (index, value) => {
      const currentTransitions = data.transition || [];
      const trimmedValue = value.trim();
      const normalizedValue = trimmedValue.replace(/\s+/g, " ");
      const newTransitions = [...currentTransitions];
      if (newTransitions[index]) {
        newTransitions[index] = {
          ...newTransitions[index],
          text: normalizedValue,
        };
      }
      updateNodeData(id, { transition: newTransitions });
    },
    [id, data.transition, updateNodeData]
  );

  useEffect(() => {}, []);

  return (
    <div
      className={cn(
        "w-[300px] group inline-flex flex-col items-start justify-start rounded-lg border-2 border-teal-400/60 bg-[#E0F7FA] shadow-sm",
        selected && "outline-2 outline-teal-500/70"
      )}
    >
      <Handle className="!top-5" type="target" position={Position.Left} />
      <header className="flex w-full items-center justify-start gap-2 px-3 pb-2 pt-3">
        <span className="shrink-0 rounded-md bg-teal-600 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
          Greeting
        </span>
        <Input
          className="nodrag nowheel border-teal-200/80 bg-white/90"
          type="text"
          value={data.title}
          placeholder="Label"
          onChange={(evt) =>
            handleNodeDataUpdate({ id, data: { title: evt.target.value } })
          }
          onBlur={(evt) => handleBlur("title", evt.target.value)}
        />
        <NodeControl id={id} selected={selected} />
      </header>
      <div className="flex w-full flex-col items-start justify-start gap-0.5 p-1.5">
        <p className="px-1 text-[11px] text-teal-800/80">
          Opening line for calls (voice + chat). Use{" "}
          <code className="rounded bg-white/80 px-0.5">{"{{aiName}}"}</code>,{" "}
          <code className="rounded bg-white/80 px-0.5">{"{{firstName}}"}</code>, etc.
        </p>
        <Textarea
          value={data.instruction}
          onChange={(evt) =>
            handleNodeDataUpdate({
              id,
              data: { instruction: evt.target.value },
            })
          }
          onBlur={(evt) => handleBlur("instruction", evt.target.value)}
          className="nodrag nowheel resize-none min-h-44 max-h-80 border-teal-200/80 bg-white"
          placeholder='Hi, this is {{aiName}} from {{businessName}}…'
        />
        <div className="mt-1 flex flex-col items-start justify-start gap-1.5 self-stretch rounded-lg bg-white/80 p-2 border border-teal-100/80">
          <div className="inline-flex h-6 w-full items-center justify-between self-stretch pb-2">
            <span className="h-5 text-sm font-normal leading-tight text-teal-700/80">
              Transition
            </span>
            <Button variant="link" onClick={transition_add} className="text-teal-700">
              Add
            </Button>
          </div>
          <div className="relative flex w-full flex-col gap-1">
            {data?.transition?.map((item, index) => (
              <div
                key={item.handleId}
                className="group/item relative flex items-center justify-start gap-1 self-stretch rounded-md bg-teal-50/80 p-2 py-3"
              >
                <span>{index + 1}</span>
                <Textarea
                  value={item.text}
                  className="nodrag nowheel resize-none bg-white min-h-24 max-h-48 border-teal-100"
                  onChange={(evt) =>
                    transition_text_onchange(index, evt.target.value)
                  }
                  onBlur={(evt) =>
                    handleTransitionBlur(index, evt.target.value)
                  }
                />
                <Button
                  variant="link"
                  className="absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer hidden group-hover/item:block !p-0 h-fit text-teal-700"
                  onClick={() => transition_del(index)}
                >
                  <SvgIcon
                    name="material-symbols-light:delete-outline"
                    className=""
                  />
                </Button>

                <Handle
                  className="!-right-4 !border-teal-500 !bg-teal-50"
                  type="source"
                  position={Position.Right}
                  id={item.handleId}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(Greeting);
